import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
    ShieldCheckIcon,
    ArrowRightStartOnRectangleIcon,
    ChevronRightIcon,
    CheckIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function debounce(fn, delay) {
    let timer = null;
    return (...args) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}

const Profile = () => {
    const { user, userProfile, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('profile');
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [freshProfile, setFreshProfile] = useState(null);
    const [message, setMessage] = useState('');
    
    // Friends management state
    const [friends, setFriends] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');

    // Scalable friends list state
    const [showFriendsModal, setShowFriendsModal] = useState(false);
    const [friendsSearch, setFriendsSearch] = useState('');

    // Account deletion state
    const [showDeleteAccount, setShowDeleteAccount] = useState(false);
    const [deletionImpact, setDeletionImpact] = useState(null);
    const [deletionConfirmText, setDeletionConfirmText] = useState('');
    const [deletingAccount, setDeletingAccount] = useState(false);

    // Debounced search function
    const debouncedSearch = React.useRef(
        debounce(async (query) => {
            if (!query.trim()) {
                setSearchResults([]);
                setSearchError('');
                return;
            }
            setSearchLoading(true);
            setSearchError('');
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/users/search/${encodeURIComponent(query)}`, {
                    headers: await getAuthHeaders()
                });
                if (response.ok) {
                    const data = await response.json();
                    setSearchResults(data.data);
                } else {
                    setSearchResults([]);
                    setSearchError('No users found');
                }
            } catch (err) {
                setSearchResults([]);
                setSearchError('Error searching users');
            } finally {
                setSearchLoading(false);
            }
        }, 400)
    ).current;

    useEffect(() => {
        debouncedSearch(searchQuery);
    }, [searchQuery, debouncedSearch]);

    // After setSearchResults in debouncedSearch or after sending a friend request
    useEffect(() => {
        if (searchResults.length > 0 && userProfile?.user_id) {
            Promise.all(
                searchResults.map(async (user) => {
                    const status = await fetchFriendshipStatus(userProfile.user_id, user.user_id);
                    return { ...user, __status: status };
                })
            ).then(updatedResults => {
                setSearchResults(updatedResults);
            });
        }
        // eslint-disable-next-line
    }, [searchResults.length, userProfile?.user_id]);

    // Add logging after fetchFriendsData
    useEffect(() => {
        if (friends || pendingRequests || sentRequests) {
            console.log('Updated friends:', friends);
            console.log('Updated pendingRequests:', pendingRequests);
            console.log('Updated sentRequests:', sentRequests);
        }
    }, [friends, pendingRequests, sentRequests]);

    // Helper to get friend status for a user
    const getFriendStatus = (userId) => {
        const debug = {
            friends: friends.map(f => f.user_id || f.friend_id),
            pending: pendingRequests.map(f => f.user_id || f.from_user_id),
            sent: sentRequests.map(f => (f.user_id || f.to_user_id)),
            self: userProfile.user_id
        };
        console.log('getFriendStatus for', userId, debug);
        if (userId === userProfile.user_id) return 'self';
        if (friends.some(f => f.user_id === userId || f.friend_id === userId)) return 'friends';
        if (pendingRequests.some(f => f.user_id === userId || f.from_user_id === userId)) return 'pending';
        if (sentRequests.filter(Boolean).some(f => f.user_id === userId || f.to_user_id === userId)) return 'sent';
        // Use __status from searchResults if available
        const found = searchResults.find(u => u.user_id === userId);
        if (found && found.__status) return found.__status;
        return 'none';
    };

    // Helper to fetch friendship status between two users
    const fetchFriendshipStatus = async (userId1, userId2) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/friends/${userId1}/status/${userId2}`, {
                headers: await getAuthHeaders()
            });
            if (response.ok) {
                const data = await response.json();
                return data.data.status;
            }
        } catch (err) {
            console.error('Error fetching friendship status:', err);
        }
        return 'none';
    };

    // Fetch latest user profile from backend
    useEffect(() => {
        const fetchProfile = async () => {
            if (!userProfile?.user_id) return;
            try {
                const token = await user.getIdToken();
                const response = await fetch(`http://localhost:5001/api/users/${userProfile.user_id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    setFreshProfile(data.data);
                    setEditForm({
                        display_name: data.data.display_name || '',
                        preferences: data.data.preferences || [],
                        dietaryRestrictions: data.data.dietary_restrictions || [],
                        travelRadius: data.data.travel_radius || '',
                        preferredAreas: data.data.preferred_areas || [],
                        budgetRange: data.data.budget_range || '',
                        preferredTimes: data.data.preferred_times || [],
                        dayPreferences: data.data.day_preferences || [],
                        comfortableGroupSize: data.data.comfortable_group_size || '',
                        customActivities: data.data.custom_activities || '',
                        customAreas: data.data.custom_areas || '',
                        customDietary: data.data.custom_dietary || ''
                    });
                }
            } catch (err) {
                // ignore
            }
        };
        fetchProfile();
    }, [user, userProfile?.user_id]);


    // Friends management functions
    const getAuthHeaders = useCallback(async () => {
        const token = await user.getIdToken();
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }, [user]);

    const fetchFriendsData = useCallback(async () => {
        if (!userProfile?.user_id) return;
        
        try {
            // Fetch friends list
            const friendsResponse = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/friends/${userProfile.user_id}`, {
                headers: await getAuthHeaders()
            });
            if (friendsResponse.ok) {
                const friendsData = await friendsResponse.json();
                setFriends(friendsData.data || []);
            }

            // Fetch pending requests
            const pendingResponse = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/friends/${userProfile.user_id}/pending`, {
                headers: await getAuthHeaders()
            });
            if (pendingResponse.ok) {
                const pendingData = await pendingResponse.json();
                setPendingRequests(pendingData.data || []);
            }

            // Fetch sent requests
            const sentResponse = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/friends/${userProfile.user_id}/sent`, {
                headers: await getAuthHeaders()
            });
            if (sentResponse.ok) {
                const sentData = await sentResponse.json();
                setSentRequests(sentData.data || []);
            }
        } catch (error) {
            console.error('Error fetching friends data:', error);
        }
    }, [userProfile, getAuthHeaders]);

    const sendFriendRequest = async (email) => {
        if (!email.trim()) {
            // setMessage('Please enter an email address'); // Removed as per edit hint
            return;
        }

        if (email.trim().toLowerCase() === userProfile?.email?.toLowerCase()) {
            // setMessage('You cannot send a friend request to yourself'); // Removed as per edit hint
            return;
        }

        setLoading(true);
        // setMessage(''); // Removed as per edit hint

        try {
            // First search for user by email
            const searchResponse = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/users/search/${email.trim()}`, {
                headers: await getAuthHeaders()
            });

            if (!searchResponse.ok) {
                // setMessage('User not found'); // Removed as per edit hint
                setLoading(false);
                return;
            }

            const searchData = await searchResponse.json();
            // If search returns a list, pick the first match
            const recipient = Array.isArray(searchData.data) ? searchData.data[0] : searchData.data;
            const recipientId = recipient.user_id;

            if (recipientId === userProfile.user_id) {
                // setMessage('You cannot send a friend request to yourself'); // Removed as per edit hint
                setLoading(false);
                return;
            }

            // Debug log
            console.log('Sending friend request:', {
                fromUserId: userProfile.user_id,
                toUserId: recipientId,
                recipient
            });

            // Check current friendship status
            const statusResponse = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/friends/${userProfile.user_id}/status/${recipientId}`, {
                headers: await getAuthHeaders()
            });

            if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                if (statusData.data.status === 'friends') {
                    // setMessage('You are already friends with this user'); // Removed as per edit hint
                    setLoading(false);
                    return;
                } else if (statusData.data.status === 'pending') {
                    // setMessage('Friend request already pending'); // Removed as per edit hint
                    setLoading(false);
                    return;
                } else if (statusData.data.status === 'blocked') {
                    // setMessage('Cannot send request to blocked user'); // Removed as per edit hint
                    setLoading(false);
                    return;
                }
            }

            // Send friend request
            const requestResponse = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/friends/request`, {
                method: 'POST',
                headers: await getAuthHeaders(),
                body: JSON.stringify({
                    fromUserId: userProfile.user_id,
                    toUserId: recipientId
                })
            });

            if (requestResponse.ok) {
                // setMessage('Friend request sent successfully!'); // Removed as per edit hint
                // setFriendEmail(''); // Removed as per edit hint
                // Optimistically add to sentRequests for instant UI feedback
                if (recipientId && recipient) {
                    setSentRequests(prev => [
                        ...prev,
                        {
                            user_id: recipientId,
                            name: recipient.name,
                            email: recipient.email,
                            display_name: recipient.display_name
                        }
                    ]);
                    // Also update searchResults to reflect 'Requested' state instantly
                    setSearchResults(prevResults => prevResults.map(user =>
                        user.user_id === recipientId ? { ...user, __justRequested: true } : user
                    ));
                }
                await fetchFriendsData(); // Refresh data
                // No longer clear searchResults or force a full search refresh here for instant UI feedback
            } else {
                const errorData = await requestResponse.json();
                setMessage(
                    (errorData && (errorData.error || errorData.message))
                        ? `Failed to send friend request: ${errorData.error || errorData.message}`
                        : 'Failed to send friend request. Please try again.'
                );
            }
        } catch (error) {
            console.error('Error sending friend request:', error);
            // setMessage('Error sending friend request'); // Removed as per edit hint
        } finally {
            setLoading(false);
        }
    };

    const handleFriendAction = async (action, friendId) => {
        try {
            let payload;
            if (action === 'block') {
                payload = {
                    userId: userProfile.user_id,
                    userToBlockId: friendId
                };
            } else if (action === 'unblock') {
                payload = {
                    userId: userProfile.user_id,
                    userToUnblockId: friendId
                };
            } else {
                payload = {
                    userId: userProfile.user_id,
                    friendId: friendId
                };
            }
            
            console.log(`🔧 Friend action: ${action}`, {
                action,
                friendId,
                userId: userProfile.user_id,
                payload
            });
            const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/friends/${action}`, {
                method: 'POST',
                headers: await getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            
            console.log(`📡 Response status: ${response.status}`);
            const responseData = await response.json();
            console.log(`📡 Response data:`, responseData);
            
            if (response.ok) {
                fetchFriendsData(); // Refresh data
                // Optionally show a message or feedback here
            } else {
                console.error(`❌ Friend action failed:`, responseData);
            }
        } catch (error) {
            console.error(`Error ${action}ing friend:`, error);
        }
    };

    useEffect(() => {
        fetchFriendsData();
    }, [fetchFriendsData]);

    // Account deletion functions
    const getDeletionImpact = async () => {
        try {
            const token = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/account/deletion-impact`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setDeletionImpact(data.data);
            } else {
                toast.error('Failed to load deletion impact');
            }
        } catch (error) {
            console.error('Error getting deletion impact:', error);
            toast.error('Failed to load deletion impact');
        }
    };

    const handleDeleteAccount = async () => {
        if (deletionConfirmText !== 'DELETE MY ACCOUNT') {
            toast.error('Please type "DELETE MY ACCOUNT" exactly to confirm');
            return;
        }

        setDeletingAccount(true);
        try {
            const token = await user.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/account`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    confirmationText: deletionConfirmText
                })
            });

            if (response.ok) {
                toast.success('Account deleted successfully');
                // Logout and redirect to login
                await logout();
                navigate('/login');
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || 'Failed to delete account');
            }
        } catch (error) {
            console.error('Error deleting account:', error);
            toast.error('Failed to delete account');
        } finally {
            setDeletingAccount(false);
        }
    };

    const openDeleteAccountModal = async () => {
        setShowDeleteAccount(true);
        setDeletionConfirmText('');
        await getDeletionImpact();
    };


    const handleSaveProfile = async () => {
        try {
            const token = await user.getIdToken();
            const response = await fetch(`http://localhost:5001/api/users/${userProfile.user_id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editForm)
            });

            if (response.ok) {
                setIsEditing(false);
                // Refresh user profile
                window.location.reload();
            }
        } catch (error) {
            console.error('Error updating profile:', error);
        }
    };

    const renderProfileTab = () => (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
        >
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
                {!isEditing ? (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    >
                        Edit Profile
                    </button>
                ) : (
                    <div className="space-x-2">
                        <button
                            onClick={() => setIsEditing(false)}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveProfile}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            Save Changes
                        </button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Basic Information</h3>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Display Name
                        </label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={editForm.display_name}
                                onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        ) : (
                            <p className="text-gray-900">{freshProfile?.display_name || 'Not set'}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <p className="text-gray-900">{userProfile?.email}</p>
                    </div>
                </div>

                {/* Preferences */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">Activity Preferences</h3>
                    <div className="flex flex-wrap gap-2">
                        {userProfile?.preferences?.map((pref, index) => {
                            // Decode HTML entities (e.g., &amp; -> &)
                            const decodedPref = pref
                                ?.replace(/&amp;/g, '&')
                                ?.replace(/&lt;/g, '<')
                                ?.replace(/&gt;/g, '>')
                                ?.replace(/&quot;/g, '"')
                                ?.replace(/&#39;/g, "'")
                                ?.replace(/&nbsp;/g, ' ') || pref;
                            return (
                                <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                                    {decodedPref}
                                </span>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* More sections can be added here */}
        </motion.div>
    );

    const renderAccountTab = () => (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
        >
            <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
            
            <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                        <ShieldCheckIcon className="w-6 h-6 text-gray-500" />
                        <div>
                            <h3 className="font-medium text-gray-900">Authentication</h3>
                            <p className="text-sm text-gray-500">Google Sign-in enabled</p>
                        </div>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </div>

                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                        <ArrowRightStartOnRectangleIcon className="w-6 h-6 text-gray-500" />
                        <div>
                            <h3 className="font-medium text-gray-900">Sign Out</h3>
                            <p className="text-sm text-gray-500">Sign out of your account</p>
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                        Sign Out
                    </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                        <TrashIcon className="w-6 h-6 text-red-500" />
                        <div>
                            <h3 className="font-medium text-red-900">Delete Account</h3>
                            <p className="text-sm text-red-700">Permanently delete your account and all data</p>
                        </div>
                    </div>
                    <button
                        onClick={openDeleteAccountModal}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center space-x-2"
                    >
                        <TrashIcon className="w-4 h-4" />
                        <span>Delete</span>
                    </button>
                </div>
            </div>
        </motion.div>
    );

    // Profile header avatar helper
    const getInitials = (name, email) => {
        if (name && name.length > 0) {
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        }
        if (email && email.length > 0) {
            return email[0].toUpperCase();
        }
        return '?';
    };

    const filteredFriends = friends.filter(f =>
      (f.display_name || f.name || '').toLowerCase().includes(friendsSearch.toLowerCase())
    );



    if (!userProfile || !freshProfile) {
        return <div>Loading...</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Profile Header */}
                <div className="flex flex-col md:flex-row items-center md:items-end md:justify-between bg-white rounded-2xl shadow p-6 mb-8 border border-gray-200">
                    <div className="flex items-center space-x-6 w-full md:w-auto">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-400 to-blue-400 flex items-center justify-center text-white text-3xl font-bold">
                            {getInitials(freshProfile.display_name || freshProfile.name, freshProfile.email)}
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-gray-900">{freshProfile.display_name || freshProfile.name || 'User'}</div>
                            <div className="text-sm text-gray-500">{freshProfile.email}</div>
                        </div>
                    </div>
                    <div className="flex space-x-8 mt-6 md:mt-0">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{friends.length}</div>
                            <div className="text-xs text-gray-600 uppercase tracking-wide">Friends</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-yellow-600">{pendingRequests.length}</div>
                            <div className="text-xs text-gray-600 uppercase tracking-wide">Pending</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{sentRequests.length}</div>
                            <div className="text-xs text-gray-600 uppercase tracking-wide">Sent</div>
                        </div>
                    </div>
                </div>

                {/* User Search Bar */}
                {message && (
                  <div className="mb-4 p-3 rounded bg-red-100 text-red-700 border border-red-200">
                    {message}
                  </div>
                )}
                <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Find People</label>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search by name or email..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    {searchLoading && <p className="text-xs text-gray-500 mt-1">Searching...</p>}
                    {searchError && <p className="text-xs text-red-500 mt-1">{searchError}</p>}
                    {searchResults.length > 0 && (
                        <div className="mt-3 bg-white border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-100">
                            {searchResults.map((user, idx) => {
                                const status = getFriendStatus(user.user_id);
                                console.log('SearchResult user:', user, 'user_id:', user.user_id, 'Status:', status, 'friends:', friends, 'pending:', pendingRequests, 'sent:', sentRequests);
                                return (
                                    <div key={user.user_id || idx} className="flex items-center justify-between px-4 py-2">
                                        <div>
                                            <div className="font-medium text-gray-900">{user.display_name || user.name || user.email}</div>
                                            <div className="text-xs text-gray-500">{user.email}</div>
                                        </div>
                                        {status === 'self' && <span className="text-xs text-gray-400">You</span>}
                                        {status === 'friends' && <span className="text-xs text-green-600 font-semibold">Friends</span>}
                                        {status === 'pending' && <span className="text-xs text-yellow-600 font-semibold">Pending</span>}
                                        {status === 'sent' && <span className="text-xs text-blue-600 font-semibold">Requested</span>}
                                        {status === 'none' && (
                                            <button
                                                onClick={() => sendFriendRequest(user.email)}
                                                className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-xs"
                                                disabled={loading}
                                            >
                                                Add Friend
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Friends, Pending, Sent Lists */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Friends List */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-medium text-green-800 mb-3">Your Friends ({friends.length})</h4>
                        {friends.length > 0 ? (
                            <>
                                <div className="space-y-2">
                                    {friends.slice(0, 5).map((friend, idx) => (
                                        <div
                                            key={friend.user_id || idx}
                                            className="flex items-center justify-between p-2 bg-white rounded border border-green-200"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="font-semibold text-sm text-gray-900 truncate">
                                                    {friend.display_name || friend.name || 'Unnamed User'}
                                                </p>
                                                {friend.email && (
                                                    <p className="text-xs text-gray-400 truncate">{friend.email}</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleFriendAction('block', friend.user_id || friend.friend_id)}
                                                className="ml-4 flex items-center px-2 py-1 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-100 transition"
                                                title="Block"
                                            >
                                                <span className="mr-1">Block</span>
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {friends.length > 5 && (
                                    <button
                                        className="mt-2 px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-xs font-medium"
                                        onClick={() => setShowFriendsModal(true)}
                                    >
                                        See All Friends
                                    </button>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-green-700">No friends yet</p>
                        )}
                    </div>
                    {/* Pending Requests */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="font-medium text-yellow-800 mb-3">Pending Requests ({pendingRequests.length})</h4>
                        {pendingRequests.length > 0 ? (
                            <div className="space-y-2">
                                {pendingRequests.map((request, idx) => (
                                    <div key={request.user_id || idx} className="flex items-center justify-between p-2 bg-white rounded border border-yellow-200">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm text-gray-900 truncate">{request.display_name || request.name || request.email}</p>
                                            <p className="text-xs text-gray-500">{request.email}</p>
                                        </div>
                                        <div className="flex space-x-1">
                                            <button
                                                onClick={() => handleFriendAction('accept', request.from_user_id)}
                                                className="p-1 bg-green-600 text-white rounded hover:bg-green-700"
                                                title="Accept"
                                            >
                                                <CheckIcon className="w-3 h-3" />
                                            </button>
                                            <button
                                                onClick={() => handleFriendAction('decline', request.from_user_id)}
                                                className="p-1 bg-red-600 text-white rounded hover:bg-red-700"
                                                title="Decline"
                                            >
                                                <XMarkIcon className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-yellow-700">No pending requests</p>
                        )}
                    </div>
                    {/* Sent Requests */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-800 mb-3">Sent Requests ({sentRequests.length})</h4>
                        {sentRequests.length > 0 ? (
                            <div className="space-y-2">
                                {sentRequests.map((request, idx) => (
                                    <div key={request.user_id || idx} className="p-2 bg-white rounded border border-blue-200">
                                        <p className="font-medium text-sm text-gray-900 truncate">{request.display_name || request.name || request.email}</p>
                                        <p className="text-xs text-blue-600">Pending...</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-blue-700">No sent requests</p>
                        )}
                    </div>
                </div>

                {/* Tabs/Sections: About/Preferences, Account Settings */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <nav className="flex space-x-8 px-6 pt-6" aria-label="Tabs">
                        <button
                            onClick={() => setActiveTab('profile')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'profile'
                                    ? 'border-purple-500 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            About & Preferences
                        </button>
                        <button
                            onClick={() => setActiveTab('account')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'account'
                                    ? 'border-purple-500 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Account Settings
                        </button>
                    </nav>
                    <div className="p-6">
                        {activeTab === 'profile' && renderProfileTab()}
                        {activeTab === 'account' && renderAccountTab()}
                    </div>
                </div>
            </div>

            {/* Friends Modal */}
            {showFriendsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[80vh] flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h5 className="font-semibold text-lg text-green-800">All Friends ({friends.length})</h5>
                            <button onClick={() => setShowFriendsModal(false)} className="text-gray-400 hover:text-gray-700 text-xl">&times;</button>
                        </div>
                        <div className="p-4 border-b">
                            <input
                                type="text"
                                placeholder="Search friends..."
                                value={friendsSearch}
                                onChange={e => setFriendsSearch(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-2">
                            {filteredFriends.length > 0 ? (
                                filteredFriends.map((friend, idx) => (
                                    <div
                                        key={friend.user_id || idx}
                                        className="flex items-center justify-between p-2 bg-white rounded border border-green-200"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm text-gray-900 truncate">
                                                {friend.display_name || friend.name || 'Unnamed User'}
                                            </p>
                                            {friend.email && (
                                                <p className="text-xs text-gray-400 truncate">{friend.email}</p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleFriendAction('block', friend.user_id || friend.friend_id)}
                                            className="ml-4 flex items-center px-2 py-1 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-100 transition"
                                            title="Block"
                                        >
                                            <span className="mr-1">Block</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728" />
                                            </svg>
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500">No friends found.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

        {/* Account Deletion Modal */}
        <AnimatePresence>
            {showDeleteAccount && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
                    >
                        <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-6">
                            <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
                        </div>
                        
                        <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                            Delete Account Permanently
                        </h2>
                        
                        <div className="text-center mb-6">
                            <p className="text-gray-600 mb-4">
                                Are you sure you want to delete your account? <strong>This action cannot be undone.</strong>
                            </p>
                            
                            {/* Deletion Impact Summary */}
                            {deletionImpact && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left mb-4">
                                    <p className="text-sm text-red-800 font-medium mb-2">This will affect:</p>
                                    <ul className="text-sm text-red-700 space-y-1">
                                        {deletionImpact.impact.ownedGroups > 0 && (
                                            <li>• <strong>{deletionImpact.impact.ownedGroups}</strong> groups you own (will be transferred or deleted)</li>
                                        )}
                                        {deletionImpact.impact.memberGroups > 0 && (
                                            <li>• <strong>{deletionImpact.impact.memberGroups}</strong> groups you're a member of</li>
                                        )}
                                        {deletionImpact.impact.friends > 0 && (
                                            <li>• <strong>{deletionImpact.impact.friends}</strong> friend relationships</li>
                                        )}
                                        {deletionImpact.impact.pendingInvitations > 0 && (
                                            <li>• <strong>{deletionImpact.impact.pendingInvitations}</strong> pending invitations</li>
                                        )}
                                        <li>• All your profile data, preferences, and settings</li>
                                        <li>• All notifications and activity history</li>
                                    </ul>
                                </div>
                            )}
                            
                            <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-left">
                                <p className="text-sm text-red-800 font-medium mb-2">Important Notes:</p>
                                <ul className="text-sm text-red-700 space-y-1">
                                    <li>• Your friends will be notified that you've left</li>
                                    <li>• Group ownership will be transferred to other admins</li>
                                    <li>• Groups with no other admins will be deleted</li>
                                    <li>• You will need to create a new account to use the service again</li>
                                </ul>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type <span className="font-mono bg-gray-100 px-2 py-1 rounded">DELETE MY ACCOUNT</span> to confirm:
                            </label>
                            <input
                                type="text"
                                value={deletionConfirmText}
                                onChange={(e) => setDeletionConfirmText(e.target.value)}
                                placeholder="Type DELETE MY ACCOUNT here..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                                autoFocus
                            />
                        </div>

                        <div className="flex space-x-4">
                            <button
                                onClick={() => {
                                    setShowDeleteAccount(false);
                                    setDeletionConfirmText('');
                                    setDeletionImpact(null);
                                }}
                                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                disabled={deletionConfirmText !== 'DELETE MY ACCOUNT' || deletingAccount}
                                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center space-x-2"
                            >
                                {deletingAccount ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Deleting...</span>
                                    </>
                                ) : (
                                    <>
                                        <TrashIcon className="w-4 h-4" />
                                        <span>Delete Account</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
        </div>
    );
};

export default Profile;
